<template>
  <div
    class="flex mb-6 animate-in"
    :class="isUser ? 'justify-end' : 'justify-start'"
  >
    <!-- Avatar asistenta -->
    <img
      v-if="!isUser"
      src="/src/assets/rich-radce-small.png"
      alt="Köhler avatar"
      class="w-9 h-9 rounded-full object-cover mt-1 border border-gray-200 shadow-sm"
      draggable="false"
    />

    <!-- Hlavní bubble -->
    <div
      class="relative px-4 py-2 pb-6 rounded-2xl text-base break-words shadow-sm group overflow-x-auto"
      :class="{
        'bg-white text-gray-900 rounded-tl-none border border-gray-200':
          !isUser,
        'bg-red-600 text-white rounded-tr-none': isUser,
        // Responzivní šířka - bezpečnější na mobilu
        'max-w-[calc(100vw-5rem)] sm:max-w-[calc(100vw-4rem)] md:max-w-[50vw]':
          !isUser,
        'max-w-[calc(100vw-5rem)] sm:max-w-[calc(100vw-4rem)] md:max-w-[50vw]':
          isUser,
      }"
    >
      <!-- Edit ikona pro AI odpovědi (pouze pro @hotjet.cz) -->
      <button
        v-if="canEdit && !isUser && !isLoading && !isError && !isStreaming && !isEditing"
        @click="startEdit"
        class="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 rounded z-10"
        title="Editovat odpověď (pouze @hotjet.cz)"
      >
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
        </svg>
      </button>

      <!-- Obsah zprávy nebo edit mód -->
      <div v-if="!isEditing" class="pt-6">
        <MessageContent
          :text="text"
          :isUser="isUser"
          :isLoading="isLoading"
          :isError="isError"
          :isStreaming="isStreaming"
          @sendMessage="(msg) => $emit('sendMessage', msg)"
        />

        <!-- Přílohy u user zprávy - na konci bubliny -->
        <div v-if="isUser && attachments && attachments.length > 0" class="mt-3 pt-2 border-t border-red-400/30">
          <div class="flex flex-wrap gap-2">
            <a
              v-for="(attachment, index) in attachments"
              :key="index"
              :href="attachment.driveUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-1.5 px-2 py-1 bg-red-500 hover:bg-red-400 rounded text-xs text-white transition-colors"
              :class="{ 'opacity-50 cursor-not-allowed pointer-events-none': !attachment.driveUrl }"
            >
              <span>{{ getAttachmentIcon(attachment.contentType) }}</span>
              <span class="truncate max-w-[120px]">{{ attachment.fileName }}</span>
            </a>
          </div>
        </div>
      </div>

      <!-- Edit mód -->
      <div v-else class="space-y-2">
        <textarea
          ref="editTextarea"
          v-model="editedText"
          @input="autoResizeTextarea"
          @keydown.ctrl.enter="saveEdit"
          @keydown.meta.enter="saveEdit"
          @keydown.esc="cancelEdit"
          :class="{
            'bg-white text-gray-900 border-gray-300 focus:ring-indigo-500': !isUser,
            'bg-red-500 text-white border-red-400 focus:ring-red-300': isUser
          }"
          class="w-full p-3 rounded border focus:outline-none focus:ring-2 resize-none min-h-[100px]"
          style="overflow-y: hidden; box-sizing: border-box; margin: 0;"
        />
        <div class="flex gap-2 justify-end">
          <button
            @click="cancelEdit"
            class="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition"
          >
            Zrušit
          </button>
          <button
            @click="saveEdit"
            :class="{
              'bg-indigo-600 hover:bg-indigo-700': !isUser,
              'bg-red-600 hover:bg-red-700': isUser
            }"
            class="px-3 py-1 text-sm text-white rounded transition"
          >
            Uložit (Ctrl+Enter)
          </button>
        </div>
      </div>

      <!-- Copy tlačítko pro AI odpovědi -->
      <MessageCopyButton
        v-if="!isLoading && !isError && !isUser"
        :text="text"
        :sources="sources"
      />

      <!-- Cache Statistics (pouze pro AI odpovědi) -->
      <div
        v-if="!isUser && cacheStats"
        class="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200"
      >
        <div class="flex flex-wrap gap-2 text-xs text-blue-700">
          <!-- Retriever info -->
          <span
            v-if="cacheStats.retrieverUsed"
            :class="[
              'px-2 py-1 rounded font-medium',
              cacheStats.retrieverUsed === 'gemini'
                ? 'bg-blue-500 text-white'
                : cacheStats.retrieverUsed === 'pinecone'
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-gray-100 text-gray-600'
            ]"
          >
            {{ cacheStats.retrieverUsed === 'gemini' ? '🔍 Google' : cacheStats.retrieverUsed === 'pinecone' ? '🌲 Pinecone' : '⚪ None' }}
          </span>
          <span class="font-medium">⚡ Cache:</span>
          <span class="bg-blue-100 px-2 py-1 rounded">
            📥 In: {{ cacheStats.inputTokens || 0 }}
          </span>
          <span
            v-if="cacheStats.cacheRead > 0"
            class="bg-green-100 px-2 py-1 rounded"
          >
            ✅ Read: {{ cacheStats.cacheRead }}
          </span>
          <span
            v-if="cacheStats.cacheCreated > 0"
            class="bg-yellow-100 px-2 py-1 rounded"
          >
            ⚡ Write: {{ cacheStats.cacheCreated }}
          </span>
          <span class="bg-purple-100 px-2 py-1 rounded">
            📤 Out: {{ tokens?.output || 0 }}
          </span>
          <!-- Gemini usage (pokud byl použit) -->
          <template v-if="cacheStats.geminiUsage">
            <span class="font-medium ml-2">🔍 Gemini:</span>
            <span class="bg-blue-50 px-2 py-1 rounded text-blue-700">
              In: {{ cacheStats.geminiUsage.promptTokens }}
            </span>
            <span class="bg-blue-50 px-2 py-1 rounded text-blue-700">
              Out: {{ cacheStats.geminiUsage.outputTokens }}
            </span>
          </template>
        </div>
      </div>

      <!-- Produkty - VYPNUTO, používáme markdown tabulky v textu -->
      <!-- <ProductList
        v-if="products && products.length > 0 && !isUser"
        :products="products"
        @productInterest="handleProductInterest"
        @productInfo="handleProductInfo"
        @requestConsultation="handleRequestConsultation"
        @requestQuote="handleRequestQuote"
        @showMoreResults="handleShowMoreResults"
      /> -->

      <!-- Zdroje (skryté v share token režimu) -->
      <MessageSources
        v-if="sources && sources.length > 0 && !isUser && !shareTokenMode"
        :sources="sources"
      />

      <!-- CTA tlačítka -->
      <MessageCTA
        v-if="cta && cta.buttons"
        :cta="cta"
        @suggestion="$emit('suggestion', $event)"
      />
    </div>

    <!-- Avatar uživatele -->
    <div
      v-if="isUser"
      class="w-9 h-9 flex items-center justify-center rounded-full mt-1 ml-3 bg-red-600 text-white font-bold text-sm select-none shadow-sm"
    >
      Já
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick } from 'vue';
import MessageContent from "./MessageContent.vue";
import MessageCopyButton from "./MessageCopyButton.vue";
import MessageSources from "./MessageSources.vue";
import MessageCTA from "./MessageCTA.vue";

const props = defineProps({
  text: String,
  isUser: {
    type: Boolean,
    default: false,
  },
  isLoading: Boolean,
  isError: Boolean,
  isStreaming: Boolean,
  cta: Object,
  tokens: [Number, Object],
  sources: Array,
  timestamp: Date,
  cacheStats: Object,
  products: Array, // NOVÉ: Produktová data
  messageId: [String, Number], // ID zprávy pro edit
  user: {
    type: Object,
    default: null,
  },
  shareTokenMode: {
    type: Boolean,
    default: false,
  },
  attachments: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["suggestion", "sendMessage", "editMessage"]);

// Kontrola, jestli má uživatel oprávnění editovat
import { computed } from 'vue';
const canEdit = computed(() => {
  if (!props.user || !props.user.email) return false;
  return props.user.email.endsWith('@hotjet.cz');
});

// Edit state
const isEditing = ref(false);
const editedText = ref('');
const editTextarea = ref(null);

// Edit functions
function startEdit() {
  isEditing.value = true;
  editedText.value = props.text;
  nextTick(() => {
    if (editTextarea.value) {
      editTextarea.value.focus();
      autoResizeTextarea();
    }
  });
}

function autoResizeTextarea() {
  if (editTextarea.value) {
    editTextarea.value.style.height = 'auto';
    editTextarea.value.style.height = editTextarea.value.scrollHeight + 'px';
  }
}

function cancelEdit() {
  isEditing.value = false;
  editedText.value = '';
}

function saveEdit() {
  if (editedText.value.trim() && editedText.value !== props.text) {
    // Nejdřív zavři edit mód a POTOM emituj změnu
    // Vue takhle stihne aktualizovat props.text před zavřením
    const newText = editedText.value.trim();
    emit('editMessage', {
      messageId: props.messageId,
      newText: newText,
      isAssistant: !props.isUser
    });
  }
  // Zavři edit mód až po emitu
  nextTick(() => {
    isEditing.value = false;
  });
}

// Helper pro ikony příloh
function getAttachmentIcon(contentType) {
  switch (contentType) {
    case 'image': return '🖼️';
    case 'pdf': return '📄';
    case 'document': return '📝';
    case 'spreadsheet': return '📊';
    case 'archive': return '📦';
    default: return '📎';
  }
}

// Handlers pro ProductList eventy
const handleProductInterest = ({ message }) => {
  emit("sendMessage", message);
};

const handleProductInfo = ({ message }) => {
  emit("sendMessage", message);
};

const handleRequestConsultation = (message) => {
  emit("sendMessage", message);
};

const handleRequestQuote = (message) => {
  emit("sendMessage", message);
};

const handleShowMoreResults = () => {
  emit("sendMessage", "Zobraz mi více výsledků tepelných čerpadel");
};
</script>

<style scoped>
/* Animace pro objevení zprávy */
.animate-in {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
